import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface StoredItem {
  key: string;
  label: string;
  value: string;
}

export interface PropertyReturnPayload {
  allReturned: boolean;
  returnedItems: string[];
  notReturned: string[];
  missingOrDamaged: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: PropertyReturnPayload) => void;
  clientName: string;
  storedAtLabel: string;
  items: StoredItem[];
}

const PropertyReturnDialog: React.FC<Props> = ({
  isOpen, onClose, onConfirm, clientName, storedAtLabel, items,
}) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [missing, setMissing] = useState('None');

  useEffect(() => {
    if (isOpen) {
      setChecked(Object.fromEntries(items.map(i => [i.key, true])));
      setMissing('None');
    }
  }, [isOpen, items]);

  const allReturned = useMemo(
    () => items.length > 0 && items.every(i => checked[i.key]),
    [items, checked]
  );

  const toggleAll = (val: boolean) => {
    setChecked(Object.fromEntries(items.map(i => [i.key, val])));
  };

  const handleConfirm = () => {
    const returnedItems = items.filter(i => checked[i.key]).map(i => `${i.label}: ${i.value}`);
    const notReturned = items.filter(i => !checked[i.key]).map(i => `${i.label}: ${i.value}`);
    onConfirm({
      allReturned,
      returnedItems,
      notReturned,
      missingOrDamaged: missing.trim() || 'None',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark Property Returned</DialogTitle>
          <DialogDescription>
            Confirm which stored belongings were returned to {clientName}. Stored {storedAtLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No itemized contents on the stored entry. Confirming will record a general return.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox
                  id="return-all"
                  checked={allReturned}
                  onCheckedChange={(v) => toggleAll(!!v)}
                />
                <Label htmlFor="return-all" className="font-medium cursor-pointer">
                  All items returned
                </Label>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.key} className="flex items-start gap-2">
                    <Checkbox
                      id={`return-${item.key}`}
                      checked={!!checked[item.key]}
                      onCheckedChange={(v) =>
                        setChecked((prev) => ({ ...prev, [item.key]: !!v }))
                      }
                      className="mt-0.5"
                    />
                    <Label htmlFor={`return-${item.key}`} className="cursor-pointer leading-snug">
                      <span className="font-medium">{item.label}:</span>{' '}
                      <span className="text-muted-foreground">{item.value}</span>
                    </Label>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="missing">Missing or damaged</Label>
            <Textarea
              id="missing"
              value={missing}
              onChange={(e) => setMissing(e.target.value)}
              placeholder="None"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm}>Confirm Return</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyReturnDialog;
